import { notification } from "../libs/api-url";
import axios from "@/libs/axios";

/**
 * Notification Service
 * Handles all admin notification center API calls.
 *
 * API Endpoints:
 * - GET /api/v1/admin/notification/list - Get notification list
 * - GET /api/v1/admin/notification/unread-count - Get unread notification count
 * - PUT /api/v1/admin/notification/read-one - Mark a single notification as read
 * - PUT /api/v1/admin/notification/read-all - Mark all notifications as read
 * - DELETE /api/v1/admin/notification/delete-all - Delete all notifications
 */
class NotificationService {
  /**
   * @name list
   * @description Fetches the notification list with pagination and read/unread filter.
   * @param {Object} params - Query parameters (page, limit, is_read)
   * @returns {Promise<Object>} The response data containing the notification list.
   */
  static async list(params = {}) {
    try {
      const response = await axios.get(notification.list, { params });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name getUnreadCount
   * @description Fetches the logged-in admin's unread notification count.
   * @returns {Promise<Object>} The response data containing the unread count.
   */
  static async getUnreadCount() {
    try {
      const response = await axios.get(notification.unreadCount);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name readOne
   * @description Marks a single notification as read.
   * @param {string|number} notificationId - Notification id
   * @returns {Promise<Object>} The response data.
   */
  static async readOne(notificationId) {
    try {
      const response = await axios.put(notification.readOne, {}, { params: { notification_id: notificationId } });
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name readAll
   * @description Marks every notification owned by the logged-in admin as read.
   * @returns {Promise<Object>} The response data.
   */
  static async readAll() {
    try {
      const response = await axios.put(notification.readAll);
      return response.data;
    } catch (err) {
      throw err;
    }
  }

  /**
   * @name deleteAll
   * @description Deletes (soft-deletes) every notification owned by the logged-in admin.
   * @returns {Promise<Object>} The response data.
   */
  static async deleteAll() {
    try {
      const response = await axios.delete(notification.deleteAll);
      return response.data;
    } catch (err) {
      throw err;
    }
  }
}

export { NotificationService };
